import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export const JoySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="section-padding bg-background-secondary relative overflow-hidden">
      <div className="container-luxury relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="text-accent font-body text-sm tracking-[0.3em] uppercase mb-4 block">
              La magie des dauphins
            </span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground leading-tight mb-8">
              Réveille ta joie intérieure grâce à l'énergie des 
              <span className="text-gradient-gold"> Maîtres Dauphins !</span>
            </h2>

            <div className="space-y-6 text-muted-foreground font-body leading-relaxed">
              <p>
                Les dauphins sont l'incarnation de la joie. Ils vivent dans le jeu, la jouissance et l'allégresse.
                Et c'est une véritable source d'inspiration pour nous êtres humains.
              </p>
              <p>
                Car au quotidien, on peut vite tomber dans des états de stress, de déprime voire même de désespoir.
                Alors qu'il existe une autre façon de vivre sa vie. <span className="text-accent">Plus douce et plus paisible.</span>
              </p>
              <p>
                Et c'est ce que tu vas découvrir dans l'école des Maîtres Dauphins.
              </p>
              <p>
                Pas besoin de passer des heures en thérapie. Ou de faire des soins énergétiques toutes les semaines.
                Je te propose de prendre un "<span className="text-accent font-semibold">raccourci quantique</span>" grâce aux fréquences delphiques.
              </p>
            </div>

            <div className="mt-8 glass-card rounded-2xl p-6">
              <p className="text-foreground font-body">
                Cette technologie est unique. Car leurs fréquences émettent des vibrations qui s'encodent directement 
                dans <span className="text-accent">tes cellules et les 60% d'eau</span> qui composent ton corps.
              </p>
              <p className="text-muted-foreground font-body mt-3">
                C'est un processus alchimique qui va te permettre de <span className="text-accent">transmuter tes ombres en lumière.</span>
              </p>
            </div>
          </motion.div>

          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Main images grid */}
            <div className="grid grid-cols-3 gap-4">
              {[
                "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_cymatique_water_geometric_frequencies_sacred_mazaru__f28ef2c9-e32a-4e5e-91f1-a3b5c99b3590.png",
                "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_pattern_flower_of_life_in_water_which_constitue_huma_854e3522-702b-409b-be98-ccb7c3914a66.png",
                "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_cymatique_water_geometric_frequencies_sacred_mazaru__209bef68-00f4-4bb0-8228-377a4de31b94.png"
              ].map((src, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="aspect-square rounded-2xl overflow-hidden border border-accent/20"
                >
                  <img src={src} alt="Cymatique" className="w-full h-full object-cover" />
                </motion.div>
              ))}
            </div>

            {/* Additional content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.7 }}
              className="mt-8"
            >
              <div className="luxury-card-gold rounded-2xl p-6">
                <p className="text-foreground font-body mb-4">
                  Et tu vas retrouver un état de bien-être <span className="text-accent font-semibold">durable.</span>
                </p>
                <p className="text-muted-foreground font-body text-sm">
                  Car tu as sans doute remarqué que lorsque tu sors d'un soin énergétique ou que tu reviens d'une retraite 
                  spirituelle : tu te sens super bien, plein·e d'énergie ! Puis les jours passent, le stress du quotidien 
                  revient et tu reviens à la case 0.
                </p>
                <p className="text-accent font-body mt-4">
                  Avec les Maîtres Dauphins, plus besoin d'une aide extérieure. Tu deviens le·la souverain·e de ton bien-être.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Banner */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9 }}
          className="mt-20"
        >
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2024/10/DALL%C2%B7E-2024-10-02-15.30.16-A-photorealistic-cinematic-banner-image-with-a-serene-divine-water-theme.-The-scene-features-a-crystal-clear-ocean-with-shimmering-water-bathed-in-s-1024x585.webp"
              alt="Ocean divin"
              className="w-full h-64 md:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-transparent flex items-center">
              <div className="p-8 md:p-12 max-w-2xl">
                <h3 className="font-display text-2xl md:text-3xl text-foreground mb-4">
                  Si tu as le sentiment de stagner, d'avoir tout essayé : 
                  <span className="text-gradient-gold"> il est temps de passer au niveau quantique !</span>
                </h3>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default JoySection;
